import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { formatImageUrl } from '../utils/formatImageUrl';

const STAGES = [
    { id: 1, key: 'placed', label: 'Order Placed', icon: 'ph-shopping-bag', desc: 'Order received & confirmed' },
    { id: 2, key: 'payment', label: 'Processing', icon: 'ph-arrows-clockwise', desc: 'Preparing for dispatch' },
    { id: 3, key: 'shipped', label: 'Shipped', icon: 'ph-package', desc: 'Handed over to courier' },
    { id: 4, key: 'out_for_delivery', label: 'Out for Delivery', icon: 'ph-truck', desc: 'With local delivery agent' },
    { id: 5, key: 'delivered', label: 'Delivered', icon: 'ph-check-circle', desc: 'Delivered to your doorstep' },
];

const getStatusBadgeStyle = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'delivered':
            return { bg: '#DCFCE7', color: '#15803D', label: 'Delivered' };
        case 'shipped':
            return { bg: '#FEF3C7', color: '#B45309', label: 'Shipped' };
        case 'processing':
            return { bg: '#E0E7FF', color: '#4338CA', label: 'Out for Delivery' };
        case 'confirmed':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Confirmed' };
        case 'cancelled':
            return { bg: '#FEE2E2', color: '#B91C1C', label: 'Cancelled' };
        case 'returned':
            return { bg: '#F3F4F6', color: '#4B5563', label: 'Returned' };
        default:
            return { bg: '#FEF9C3', color: '#854D0E', label: 'Pending' };
    }
};

const getReturnStatusBadge = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'refund_completed':
            return { bg: '#DCFCE7', color: '#15803D', label: 'Refund Completed' };
        case 'replacement_delivered':
            return { bg: '#DCFCE7', color: '#15803D', label: 'Replacement Delivered' };
        case 'approved':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Return Approved' };
        case 'pickup_scheduled':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Pickup Scheduled' };
        case 'picked_up':
            return { bg: '#E0E7FF', color: '#4338CA', label: 'Item Picked Up' };
        case 'inspection_completed':
            return { bg: '#FEF3C7', color: '#B45309', label: 'QC Inspection Passed' };
        case 'refund_initiated':
            return { bg: '#FEF3C7', color: '#B45309', label: 'Refund Initiated' };
        case 'replacement_shipped':
            return { bg: '#DBEAFE', color: '#1D4ED8', label: 'Replacement Dispatched' };
        case 'rejected':
            return { bg: '#FEE2E2', color: '#B91C1C', label: 'Return Rejected' };
        default:
            return { bg: '#FEF9C3', color: '#854D0E', label: 'Under Review' };
    }
};

const getReturnStageNumber = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'pending_review':
            return 1;
        case 'approved':
            return 2;
        case 'pickup_scheduled':
        case 'picked_up':
            return 3;
        case 'inspection_completed':
        case 'refund_initiated':
        case 'replacement_shipped':
            return 4;
        case 'refund_completed':
        case 'replacement_delivered':
            return 5;
        case 'rejected':
            return -1;
        default:
            return 1;
    }
};

const RETURN_STAGES = [
    { id: 1, label: 'Submitted', desc: 'Request received' },
    { id: 2, label: 'Approved', desc: 'Review passed' },
    { id: 3, label: 'Pickup', desc: 'Reverse transit' },
    { id: 4, label: 'Inspection', desc: 'QC check' },
    { id: 5, label: 'Completed', desc: 'Refund Settled' },
];

const OrderDetails = () => {
    const { id } = useParams();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Return Modal State
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedReturnItem, setSelectedReturnItem] = useState(null);
    const [returnReason, setReturnReason] = useState('Damaged Product');
    const [returnComments, setReturnComments] = useState('');
    const [returnType, setReturnType] = useState('refund');
    const [returnVideoFile, setReturnVideoFile] = useState(null);
    const [returnVideoPreview, setReturnVideoPreview] = useState(null);
    const [previewVideoModal, setPreviewVideoModal] = useState(null);
    const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);

    const fetchOrderDetails = useCallback(async () => {
        if (!id) {
            setError('No order ID provided');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const res = await api.get(`/api/orders/${id}`);
            if (res.data) {
                setOrder(res.data);
                setError(null);
            } else {
                setError('Order not found');
            }
        } catch (err) {
            console.error('Failed to fetch order details:', err);
            setError(err.response?.data?.message || 'Could not find order details. Please check My Orders.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOrderDetails();
    }, [fetchOrderDetails]);

    const isOrderCancellable = (orderData) => {
        if (!orderData || !orderData.createdAt) return false;
        const status = (orderData.status || '').toLowerCase();
        if (!['pending', 'confirmed'].includes(status)) return false;
        const orderTime = new Date(orderData.createdAt).getTime();
        if (isNaN(orderTime)) return false;
        const hours = (Date.now() - orderTime) / (1000 * 60 * 60);
        return hours >= 0 && hours <= 2;
    };

    const getCancellationTimeRemaining = (orderData) => {
        if (!orderData || !orderData.createdAt) return null;
        const orderTime = new Date(orderData.createdAt).getTime();
        if (isNaN(orderTime)) return null;
        const elapsedMs = Date.now() - orderTime;
        const remainingMs = (2 * 60 * 60 * 1000) - elapsedMs;
        if (remainingMs <= 0) return null;
        const hours = Math.floor(remainingMs / (1000 * 60 * 60));
        const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) {
            return `${hours}h ${minutes}m left to cancel`;
        }
        return `${minutes}m left to cancel`;
    };

    const getActiveStageNumber = (orderData) => {
        if (!orderData) return 1;
        const status = (orderData.status || '').toLowerCase();
        if (status === 'cancelled') return -1;
        if (status === 'delivered') return 5;
        if (status === 'processing' || status === 'delivery') return 4;
        if (status === 'shipped') return 3;
        if (status === 'confirmed') return 2;

        const isPrepaid = (orderData.paymentMethod || '').toLowerCase() !== 'cod' && (orderData.paymentType || '').toUpperCase() !== 'FULL_COD';
        const isPaid = (orderData.paymentStatus || '').toLowerCase() === 'completed' || (orderData.paymentStatus || '').toLowerCase() === 'paid';
        if (isPaid || isPrepaid) return 2;

        return 1;
    };

    // Execute Order Cancellation
    const executeCancelOrder = async () => {
        if (!order) return;
        const loadingToast = toast.loading("Cancelling order...");
        try {
            await api.patch(`/api/returns/cancel-order/${order.id}`);
            toast.dismiss(loadingToast);
            toast.success("Order cancelled successfully!");
            fetchOrderDetails();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || "Failed to cancel order");
        }
    };

    // Trigger Toast Confirmation
    const handleCancelOrder = () => {
        if (!order) return;
        const orderRef = order.referenceSlug || order.order_number || order.id;

        toast((t) => (
            <div style={{ minWidth: '250px' }}>
                <div className="d-flex align-items-center gap-8 mb-6">
                    <i className="ph-bold ph-warning-circle text-danger-600" style={{ fontSize: '20px' }} />
                    <span className="fw-bold text-neutral-900" style={{ fontSize: '14px' }}>Cancel Order #{orderRef}?</span>
                </div>
                <p className="text-xs text-gray-600 mb-12">
                    Are you sure you want to cancel this order?
                </p>
                <div className="d-flex align-items-center justify-content-end gap-8">
                    <button
                        type="button"
                        className="py-6 px-12 rounded-6 text-xs fw-semibold"
                        style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', color: '#334155', cursor: 'pointer' }}
                        onClick={() => toast.dismiss(t.id)}
                    >
                        Keep Order
                    </button>
                    <button
                        type="button"
                        className="py-6 px-14 rounded-6 text-xs fw-bold"
                        style={{ background: '#DC2626', border: 'none', color: '#FFFFFF', cursor: 'pointer' }}
                        onClick={() => {
                            toast.dismiss(t.id);
                            executeCancelOrder();
                        }}
                    >
                        Yes, Cancel
                    </button>
                </div>
            </div>
        ), {
            duration: 8000,
            style: {
                borderRadius: '12px',
                background: '#FFFFFF',
                color: '#1E293B',
                boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.2), 0 0 0 1px rgba(15, 23, 42, 0.05)',
                padding: '14px 16px',
            },
        });
    };

    // Video file handlers
    const handleVideoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Max 50MB
        if (file.size > 50 * 1024 * 1024) {
            toast.error("Video file is too large. Maximum allowed size is 50MB.");
            return;
        }

        if (!file.type.startsWith('video/')) {
            toast.error("Please upload a valid video file (MP4 or MOV).");
            return;
        }

        setReturnVideoFile(file);
        if (returnVideoPreview) {
            URL.revokeObjectURL(returnVideoPreview);
        }
        setReturnVideoPreview(URL.createObjectURL(file));
    };

    const handleRemoveVideo = () => {
        if (returnVideoPreview) {
            URL.revokeObjectURL(returnVideoPreview);
        }
        setReturnVideoFile(null);
        setReturnVideoPreview(null);
    };

    // Submit Return Request
    const handleRequestReturn = async (e) => {
        e.preventDefault();
        if (!order) return;

        if (!returnVideoFile) {
            toast.error("An unboxing video is mandatory for return requests.");
            return;
        }

        setIsSubmittingReturn(true);
        try {
            const itemToReturn = selectedReturnItem || order.items?.[0];
            const formData = new FormData();
            formData.append('orderId', order.id);
            formData.append('orderItemId', itemToReturn?.id || '');
            formData.append('returnType', returnType);
            formData.append('reason', returnReason);
            formData.append('comments', returnComments);
            formData.append('return_quantity', itemToReturn?.quantity || 1);
            formData.append('video', returnVideoFile);

            await api.post('/api/returns', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.success("Return request submitted successfully! Our team will review it.");
            setShowReturnModal(false);
            setSelectedReturnItem(null);
            setReturnComments('');
            handleRemoveVideo();
            fetchOrderDetails();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to submit return request");
        } finally {
            setIsSubmittingReturn(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="py-80 text-center container">
                <div className="spinner-border text-main-600 mb-16" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-neutral-800">Loading Order Details...</h5>
                <p className="text-gray-500">Please wait while we retrieve the details for this order.</p>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="py-80 text-center container">
                <div className="flex-center rounded-circle bg-danger-50 text-danger-600 mx-auto mb-20" style={{ width: 80, height: 80 }}>
                    <i className="ph ph-warning-circle" style={{ fontSize: 42 }} />
                </div>
                <h4 className="fw-bold text-neutral-900 mb-12">Unable to Find Order</h4>
                <p className="text-gray-600 mb-28 max-w-500 mx-auto">{error || "We couldn't retrieve the details for this order."}</p>
                <div className="flex-center gap-16 flex-wrap">
                    <Link to="/account?tab=orders" className="btn btn-main py-12 px-28 rounded-8">
                        Back to My Orders
                    </Link>
                    <Link to="/shop" className="btn btn-outline-main py-12 px-28 rounded-8">
                        Continue Shopping
                    </Link>
                </div>
            </div>
        );
    }

    const items = order.items || [];
    const orderReturns = order.returns || items.flatMap(i => (Array.isArray(i.returns) ? i.returns : (i.returns ? [i.returns] : []))).filter(Boolean);
    const shippingAddr = order.shippingAddress || {};
    const formattedDate = order.createdAt
        ? new Date(order.createdAt).toLocaleDateString('en-IN', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
        : 'N/A';

    const orderNumber = order.referenceSlug || order.order_number || order.id;
    const isCod = (order.paymentMethod || '').toLowerCase() === 'cod';
    const statusInfo = getStatusBadgeStyle(order.status);
    const isCancelled = (order.status || '').toLowerCase() === 'cancelled';
    const canCancel = isOrderCancellable(order);
    const timeRemaining = canCancel ? getCancellationTimeRemaining(order) : null;
    const canReturn = order.status === 'delivered';
    const currentStage = getActiveStageNumber(order);

    const finalTotal = parseFloat(order.totalAmount || 0);
    const subtotal = parseFloat(order.subtotal || items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * (item.quantity || 1)), 0));
    const shippingCharge = parseFloat(order.shippingCharge || 0);
    const couponDiscount = parseFloat(order.couponDiscount || 0);

    return (
        <section className="order-details-section py-40 py-sm-60">
            <style>{`
                .od-card {
                    background: #ffffff;
                    border: 1px solid #E5E7EB;
                    border-radius: 16px;
                    padding: 16px 14px;
                    margin-bottom: 20px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
                }
                @media (min-width: 576px) {
                    .od-card {
                        padding: 22px 20px;
                        margin-bottom: 24px;
                    }
                }
                @media (min-width: 768px) {
                    .od-card {
                        padding: 28px 26px;
                    }
                }
                .od-card-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 14px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #F1F5F9;
                    margin-bottom: 16px;
                }
                @media (max-width: 575px) {
                    .od-card-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 10px;
                    }
                    .od-header-left {
                        width: 100%;
                    }
                    .od-header-right {
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding-top: 10px;
                        border-top: 1px dashed #F1F5F9;
                        text-align: left !important;
                    }
                    .od-order-title {
                        font-size: 18px !important;
                        word-break: break-all;
                    }
                }
                .od-card-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #0F172A;
                    margin-bottom: 0;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                @media (min-width: 576px) {
                    .od-card-title {
                        font-size: 17px;
                    }
                }
                .od-status-badge {
                    font-size: 12px;
                    font-weight: 700;
                    text-transform: uppercase;
                    padding: 4px 12px;
                    border-radius: 999px;
                    letter-spacing: 0.03em;
                }
                .stage-tracker-box {
                    padding: 8px 0;
                }
                .od-stepper {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    position: relative;
                }
                .od-stepper::before {
                    content: '';
                    position: absolute;
                    top: 24px;
                    left: 35px;
                    right: 35px;
                    height: 3px;
                    background: #E2E8F0;
                    z-index: 1;
                }
                .od-step {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    flex: 1;
                    padding: 0 6px;
                }
                .od-step-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    background: #F8FAFC;
                    border: 2px solid #CBD5E1;
                    color: #94A3B8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    margin-bottom: 10px;
                    transition: all 0.3s ease;
                }
                .od-step.completed .od-step-icon {
                    background: #2FA84F;
                    border-color: #2FA84F;
                    color: #ffffff;
                    box-shadow: 0 4px 10px rgba(47, 168, 79, 0.25);
                }
                .od-step.current .od-step-icon {
                    background: #ffffff;
                    border-color: #2FA84F;
                    color: #2FA84F;
                    box-shadow: 0 0 0 4px rgba(47, 168, 79, 0.15);
                }
                .od-step-label {
                    font-size: 13px;
                    font-weight: 700;
                    color: #64748B;
                    margin-bottom: 3px;
                }
                .od-step.completed .od-step-label,
                .od-step.current .od-step-label {
                    color: #0F172A;
                }
                .od-step-desc {
                    font-size: 11px;
                    color: #94A3B8;
                    line-height: 1.3;
                    max-width: 120px;
                }
                @media (max-width: 767px) {
                    .od-stepper {
                        flex-direction: column;
                        gap: 14px;
                    }
                    .od-stepper::before {
                        top: 20px;
                        bottom: 20px;
                        left: 20px;
                        right: auto;
                        width: 2px;
                        height: auto;
                    }
                    .od-step {
                        flex-direction: row;
                        align-items: center;
                        text-align: left;
                        gap: 14px;
                        width: 100%;
                        padding: 0;
                    }
                    .od-step-icon {
                        margin-bottom: 0;
                        flex-shrink: 0;
                        width: 42px;
                        height: 42px;
                        font-size: 18px;
                    }
                    .od-step-desc {
                        max-width: 100%;
                    }
                }
                .od-item-card {
                    display: flex;
                    align-items: flex-start;
                    gap: 14px;
                    padding: 16px 0;
                    border-bottom: 1px solid #F1F5F9;
                }
                .od-item-card:last-child {
                    border-bottom: none;
                    padding-bottom: 0;
                }
                .od-item-card:first-child {
                    padding-top: 0;
                }
                .od-item-thumb {
                    width: 76px;
                    height: 76px;
                    border-radius: 12px;
                    object-fit: contain;
                    background: #F8FAFC;
                    border: 1px solid #E2E8F0;
                    flex-shrink: 0;
                }
                .od-item-info {
                    flex: 1 1 0%;
                    min-width: 0;
                }
                .od-item-meta {
                    text-align: right;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 6px;
                }
                @media (max-width: 575px) {
                    .od-item-card {
                        flex-wrap: wrap;
                        gap: 12px;
                        padding: 14px 0;
                    }
                    .od-item-thumb {
                        width: 64px;
                        height: 64px;
                        border-radius: 10px;
                    }
                    .od-item-info {
                        flex: 1 1 calc(100% - 78px);
                        min-width: 0;
                    }
                    .od-item-meta {
                        width: 100%;
                        flex-direction: row;
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: wrap;
                        gap: 8px;
                        padding-top: 10px;
                        margin-top: 4px;
                        border-top: 1px dashed #F1F5F9;
                        text-align: left;
                    }
                    .od-item-meta .od-item-total {
                        font-size: 15px;
                    }
                    .od-item-meta .od-item-actions-wrap {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        flex-wrap: wrap;
                        justify-content: flex-end;
                    }
                }
                .od-info-block {
                    background: #F8FAFC;
                    border-radius: 12px;
                    padding: 16px;
                    border: 1px solid #E2E8F0;
                    height: 100%;
                    word-break: break-word;
                    overflow-wrap: anywhere;
                }
                .od-info-label {
                    font-size: 12px;
                    text-transform: uppercase;
                    color: #64748B;
                    font-weight: 700;
                    letter-spacing: 0.04em;
                    margin-bottom: 8px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .od-info-val {
                    font-size: 14px;
                    color: #0F172A;
                    font-weight: 500;
                    line-height: 1.5;
                }
                .return-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(15, 23, 42, 0.65);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    backdrop-filter: blur(6px);
                    padding: 16px;
                }
                .return-modal-box {
                    background: #ffffff;
                    width: 540px;
                    max-width: 100%;
                    border-radius: 20px;
                    padding: 28px 24px;
                    box-shadow: 0 20px 50px -10px rgba(15, 23, 42, 0.25);
                    border: 1px solid #E2E8F0;
                }
                @media (max-width: 575px) {
                    .return-modal-box {
                        padding: 20px 16px;
                        border-radius: 16px;
                    }
                }

                /* Modern Return & Refund UI */
                .rt-card {
                    border: 1px solid #E2E8F0;
                    border-radius: 16px;
                    background: #FFFFFF;
                    box-shadow: 0 2px 10px -2px rgba(15, 23, 42, 0.04);
                    margin-bottom: 24px;
                    overflow: hidden;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .rt-card:hover {
                    border-color: #CBD5E1;
                    box-shadow: 0 6px 20px -4px rgba(15, 23, 42, 0.08);
                }
                .rt-card-top {
                    padding: 16px 22px;
                    background: #F8FAFC;
                    border-bottom: 1px solid #E2E8F0;
                }
                .rt-pill-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    display: inline-block;
                    margin-right: 6px;
                }
                .rt-stepper-wrap {
                    padding: 24px 22px 20px;
                    background: #FFFFFF;
                    border-bottom: 1px solid #F1F5F9;
                }
                .rt-stepper-line {
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }
                .rt-stepper-track-bg {
                    position: absolute;
                    top: 15px;
                    left: 40px;
                    right: 40px;
                    height: 3px;
                    background: #E2E8F0;
                    z-index: 1;
                    border-radius: 3px;
                }
                .rt-stepper-track-fill {
                    position: absolute;
                    top: 15px;
                    left: 40px;
                    height: 3px;
                    background: linear-gradient(90deg, #10B981 0%, #059669 100%);
                    z-index: 1;
                    border-radius: 3px;
                    transition: width 0.4s ease;
                }
                .rt-step-item {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    flex: 1;
                    padding: 0 6px;
                }
                .rt-step-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: #FFFFFF;
                    border: 2px solid #CBD5E1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 700;
                    color: #64748B;
                    margin-bottom: 8px;
                    transition: all 0.25s ease;
                }
                .rt-step-circle.done {
                    background: #10B981;
                    border-color: #10B981;
                    color: #FFFFFF;
                    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
                }
                .rt-step-circle.active {
                    background: #FFFFFF;
                    border-color: #10B981;
                    color: #059669;
                    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18);
                    font-weight: 800;
                }
                .rt-node-title {
                    font-size: 12px;
                    font-weight: 700;
                    color: #0F172A;
                    margin-bottom: 2px;
                    line-height: 1.3;
                }
                .rt-node-desc {
                    font-size: 11px;
                    color: #64748B;
                    line-height: 1.3;
                }

                .rt-product-box {
                    padding: 16px;
                    border-radius: 12px;
                    background: #FFFFFF;
                    border: 1px solid #E2E8F0;
                    transition: border-color 0.2s ease;
                }
                .rt-product-thumb {
                    width: 68px;
                    height: 68px;
                    border-radius: 10px;
                    border: 1px solid #E2E8F0;
                    background: #FFFFFF;
                    object-fit: contain;
                    flex-shrink: 0;
                    padding: 4px;
                }
                .rt-info-card {
                    padding: 16px;
                    border-radius: 12px;
                    background: #FFFFFF;
                    border: 1px solid #E2E8F0;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                @media (max-width: 640px) {
                    .rt-card-top {
                        padding: 14px 16px;
                    }
                    .rt-stepper-wrap {
                        padding: 16px 14px;
                    }
                    .rt-stepper-line {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 16px;
                        padding-left: 6px;
                    }
                    .rt-stepper-track-bg, .rt-stepper-track-fill {
                        top: 14px;
                        bottom: 14px;
                        left: 21px;
                        width: 2px;
                        height: auto;
                        right: auto;
                    }
                    .rt-step-item {
                        flex-direction: row;
                        align-items: flex-start;
                        text-align: left;
                        gap: 14px;
                        width: 100%;
                        padding: 0;
                    }
                    .rt-step-circle {
                        margin-bottom: 0;
                        width: 28px;
                        height: 28px;
                        font-size: 11px;
                        flex-shrink: 0;
                    }
                    .rt-node-title {
                        font-size: 13px;
                        font-weight: 700;
                        color: #0F172A;
                        line-height: 1.3;
                    }
                    .rt-node-desc {
                        font-size: 11px;
                        color: #64748B;
                        line-height: 1.3;
                    }
                }
                .btn-cancel-order {
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 6px !important;
                    padding: 8px 16px !important;
                    border-radius: 8px !important;
                    font-size: 12px !important;
                    font-weight: 700 !important;
                    line-height: 1 !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    border: 1.5px solid #EF4444 !important;
                    background-color: #FEF2F2 !important;
                    color: #DC2626 !important;
                    text-decoration: none !important;
                }
                .btn-cancel-order:hover {
                    background-color: #DC2626 !important;
                    border-color: #DC2626 !important;
                    color: #FFFFFF !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 8px rgba(220, 38, 38, 0.25) !important;
                }
                .btn-cancel-order i {
                    color: inherit !important;
                    font-size: 14px !important;
                }
                @media print {
                    header, footer, .breadcrumb, .shipping, .od-actions-bar, .bottom-footer, .btn, .no-print {
                        display: none !important;
                    }
                    .od-card, .rt-card {
                        box-shadow: none !important;
                        border: 1px solid #ccc !important;
                        page-break-inside: avoid;
                    }
                    body {
                        background: #fff !important;
                    }
                }
            `}</style>

            {/* Video Lightbox Modal */}
            {previewVideoModal && (
                <div className="return-modal-overlay" onClick={() => setPreviewVideoModal(null)}>
                    <div className="return-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, background: '#000', padding: 16 }}>
                        <div className="flex-between mb-12 text-white">
                            <span className="text-sm fw-bold">Unboxing Video Proof</span>
                            <button type="button" className="btn p-0 text-white" onClick={() => setPreviewVideoModal(null)}>✕</button>
                        </div>
                        <video controls autoPlay src={previewVideoModal} className="w-100 rounded-8" style={{ maxHeight: '70vh' }} />
                    </div>
                </div>
            )}

            {/* Return Modal (Disabled) */}
            {false && showReturnModal && (
                <div className="return-modal-overlay" onClick={() => { setShowReturnModal(false); handleRemoveVideo(); }}>
                    <div className="return-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="flex-between mb-20">
                            <h5 className="mb-0">Request Return / Refund</h5>
                            <button type="button" className="btn p-0 text-gray-500" onClick={() => { setShowReturnModal(false); handleRemoveVideo(); }}>✕</button>
                        </div>
                        <p className="text-sm text-gray-600 mb-20">
                            Order <strong>#{orderNumber}</strong> {selectedReturnItem ? `• ${selectedReturnItem.productName}` : ''}
                        </p>
                        <form onSubmit={handleRequestReturn}>
                            <div className="mb-16">
                                <label className="text-sm fw-medium mb-6 d-block">Reason for Return</label>
                                <select className="common-input" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}>
                                    <option value="Damaged Product">Damaged or Leaking Item</option>
                                    <option value="Wrong Item Delivered">Wrong Item Delivered</option>
                                    <option value="Quality Not Satisfactory">Quality Not Satisfactory</option>
                                    <option value="Missing Item">Missing Item</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            {/* Mandatory Unboxing Video Field */}
                            <div className="mb-16">
                                <label className="text-sm fw-bold text-neutral-900 mb-6 d-flex align-items-center justify-content-between">
                                    <span>
                                        Unboxing Video <span className="text-danger-600">*</span>
                                    </span>
                                    <span className="text-xs text-gray-500 font-normal">Max 50MB (MP4, MOV)</span>
                                </label>

                                {!returnVideoPreview ? (
                                    <label
                                        className="d-flex flex-column align-items-center justify-content-center p-20 rounded-12 border border-gray-300 bg-gray-50 text-center cursor-pointer transition hover-border-main-600"
                                        style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                    >
                                        <input
                                            type="file"
                                            accept="video/mp4,video/quicktime,video/*"
                                            className="d-none"
                                            onChange={handleVideoChange}
                                            required
                                        />
                                        <div className="flex-center rounded-circle bg-white shadow-xs mb-8" style={{ width: 44, height: 44, color: '#2FA84F' }}>
                                            <i className="ph-fill ph-video-camera text-2xl" />
                                        </div>
                                        <span className="text-xs fw-bold text-neutral-900 mb-2">Click or drag package unboxing video here</span>
                                        <span className="text-[11px] text-gray-500">Unboxing video is mandatory showing the intact outer box & package opening</span>
                                    </label>
                                ) : (
                                    <div className="rounded-12 border border-gray-200 overflow-hidden bg-black p-8">
                                        <video
                                            controls
                                            src={returnVideoPreview}
                                            className="w-100 rounded-8"
                                            style={{ maxHeight: '200px', objectFit: 'contain', background: '#000' }}
                                        />
                                        <div className="d-flex align-items-center justify-content-between p-8 bg-white rounded-8 mt-6">
                                            <div className="d-flex align-items-center gap-8 min-w-0">
                                                <i className="ph-bold ph-video text-main-600 text-lg flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <span className="text-xs fw-bold text-neutral-900 text-truncate d-block">
                                                        {returnVideoFile?.name}
                                                    </span>
                                                    <span className="text-[11px] text-gray-500">
                                                        {(returnVideoFile?.size / (1024 * 1024)).toFixed(2)} MB
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger py-4 px-10 rounded-6 text-xs fw-bold"
                                                onClick={handleRemoveVideo}
                                            >
                                                <i className="ph ph-trash me-4" /> Remove
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mb-20">
                                <label className="text-sm fw-medium mb-6 d-block">Additional Comments</label>
                                <textarea className="common-input" rows={3} placeholder="Please describe the issue in detail..." value={returnComments} onChange={(e) => setReturnComments(e.target.value)} required />
                            </div>
                            <div className="flex-align gap-12 justify-content-end">
                                <button type="button" className="btn py-10 px-20 rounded-8 border border-gray-200" onClick={() => { setShowReturnModal(false); handleRemoveVideo(); }}>Cancel</button>
                                <button type="submit" className="btn btn-main py-10 px-24 rounded-8" disabled={isSubmittingReturn}>
                                    {isSubmittingReturn ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="container container-lg">
                {/* Navigation Back Bar */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-20 no-print">
                    <Link
                        to="/account?tab=orders"
                        className="btn btn-outline-main py-8 px-16 rounded-8 text-sm fw-bold d-inline-flex align-items-center gap-8"
                    >
                        <i className="ph-bold ph-arrow-left" /> Back to My Orders
                    </Link>

                    <div className="d-flex align-items-center gap-10 flex-wrap">
                        {canCancel && (
                            <button
                                type="button"
                                onClick={handleCancelOrder}
                                className="btn-cancel-order"
                            >
                                <i className="ph-bold ph-x-circle" /> Cancel Order
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="btn border border-gray-200 py-8 px-16 rounded-8 text-neutral-800 text-sm fw-semibold d-inline-flex align-items-center gap-6 bg-white shadow-xs hover-border-main-600"
                        >
                            <i className="ph ph-printer text-base" /> Print Receipt
                        </button>
                    </div>
                </div>

                {/* Main Order Header Card */}
                <div className="od-card">
                    <div className="od-card-header">
                        <div className="od-header-left">
                            <div className="d-flex align-items-center gap-10 flex-wrap">
                                <h4 className="fw-bold text-neutral-900 mb-0 od-order-title" style={{ fontSize: '20px' }}>
                                    Order #{orderNumber}
                                </h4>
                                <span className="od-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
                                    {statusInfo.label}
                                </span>
                                {canCancel && timeRemaining && (
                                    <span className="badge rounded-pill bg-danger-50 text-danger-700 border border-danger-200 py-4 px-10 text-xs fw-bold d-inline-flex align-items-center gap-4">
                                        <i className="ph-bold ph-clock text-xs" /> {timeRemaining}
                                    </span>
                                )}
                            </div>
                            <span className="text-xs text-gray-500 mt-6 d-block">
                                Placed on {formattedDate}
                            </span>
                        </div>

                        <div className="od-header-right text-sm-end">
                            <span className="fw-bold text-main-600 text-2xl d-block">
                                ₹{finalTotal.toFixed(2)}
                            </span>
                            <span className="text-xs text-gray-500 text-uppercase fw-semibold">
                                {isCod ? 'Cash on Delivery' : 'Prepaid (Online)'}
                            </span>
                        </div>
                    </div>

                    {/* Order Tracking Progress */}
                    {isCancelled ? (
                        <div className="alert alert-danger mb-0 rounded-12 p-16 flex-align gap-12">
                            <i className="ph ph-x-circle text-2xl flex-shrink-0" />
                            <div>
                                <strong className="d-block mb-2">This order was cancelled.</strong>
                                <span className="text-sm">If any payment was collected, a refund has been initiated to your original payment method.</span>
                            </div>
                        </div>
                    ) : (
                        <div className="stage-tracker-box">
                            <div className="od-stepper">
                                {STAGES.map((stage) => {
                                    const isCompleted = currentStage > stage.id || (currentStage === 5 && stage.id === 5);
                                    const isCurrent = currentStage === stage.id && currentStage < 5;
                                    const stepClass = isCompleted ? 'completed' : (isCurrent ? 'current' : 'pending');

                                    return (
                                        <div key={stage.id} className={`od-step ${stepClass}`}>
                                            <div className="od-step-icon">
                                                {isCompleted ? (
                                                    <i className="ph-bold ph-check" />
                                                ) : (
                                                    <i className={`ph ${stage.icon}`} />
                                                )}
                                            </div>
                                            <div>
                                                <div className="od-step-label">{stage.label}</div>
                                                <div className="od-step-desc">{stage.desc}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Items and Summary Row */}
                <div className="row gy-4">
                    {/* Left Column: Items List */}
                    <div className="col-lg-7">
                        <div className="od-card">
                            <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom border-gray-100">
                                <h6 className="od-card-title">
                                    <i className="ph ph-package text-main-600 text-xl" />
                                    <span>Items in this Order ({items.length})</span>
                                </h6>
                            </div>

                            <div className="od-items-list">
                                {items.map((item, idx) => {
                                    const itemPrice = parseFloat(item.price || 0);
                                    const itemQty = item.quantity || 1;
                                    const itemTotal = itemPrice * itemQty;
                                    const productUrl = item.product?.slug || item.product?.referenceSlug || item.productId ? `/product-details/${item.product?.slug || item.product?.referenceSlug || item.productId}` : null;
                                    const itemReturn = orderReturns.find(r => r.orderItemId === item.id || (item.returns && item.returns.some(ir => ir.id === r.id)));

                                    return (
                                        <div className="od-item-card" key={item.id || idx}>
                                            <img
                                                src={formatImageUrl(item.image)}
                                                alt={item.productName || item.name}
                                                className="od-item-thumb"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/assets/images/thumbs/product-img1.png';
                                                }}
                                            />
                                            <div className="od-item-info">
                                                <h6 className="text-sm fw-bold text-neutral-900 mb-4 text-line-2">
                                                    {productUrl ? (
                                                        <Link to={productUrl} className="text-neutral-900 hover-text-main-600 transition">
                                                            {item.productName || item.name || item.comboName || 'Healthy Homes Product'}
                                                        </Link>
                                                    ) : (
                                                        item.productName || item.name || item.comboName || 'Healthy Homes Product'
                                                    )}
                                                </h6>
                                                {item.selectedVariantName && (
                                                    <span className="text-xs text-gray-500 d-block mb-4">
                                                        Variant: <strong className="text-neutral-800">{item.selectedVariantName}</strong>
                                                    </span>
                                                )}
                                                {(item.selectedProductColor || item.selectedProductSize) && (
                                                    <span className="text-xs text-gray-500 d-block mb-4">
                                                        {item.selectedProductColor && <span>Color: {item.selectedProductColor} </span>}
                                                        {item.selectedProductSize && <span>Size: {item.selectedProductSize}</span>}
                                                    </span>
                                                )}
                                                <div className="flex-align gap-8 text-xs text-gray-500">
                                                    <span>Qty: <strong className="text-neutral-800">{itemQty}</strong></span>
                                                    <span>×</span>
                                                    <span>₹{itemPrice.toFixed(2)}</span>
                                                </div>
                                            </div>

                                            <div className="od-item-meta">
                                                <span className="fw-bold text-neutral-900 text-base d-block od-item-total">
                                                    ₹{itemTotal.toFixed(2)}
                                                </span>
                                                <div className="od-item-actions-wrap">
                                                    {/* itemReturn ? (
                                                        <span
                                                            className="badge rounded-pill d-inline-block"
                                                            style={{
                                                                background: getReturnStatusBadge(itemReturn.status).bg,
                                                                color: getReturnStatusBadge(itemReturn.status).color,
                                                                fontSize: '11px',
                                                                fontWeight: 700,
                                                                padding: '4px 10px',
                                                                whiteSpace: 'normal',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            Return: {getReturnStatusBadge(itemReturn.status).label}
                                                        </span>
                                                    ) : null */}
                                                    {/* canReturn ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm text-xs text-main-600 p-0 text-decoration-underline"
                                                            onClick={() => {
                                                                setSelectedReturnItem(item);
                                                                setShowReturnModal(true);
                                                            }}
                                                        >
                                                            Return Item
                                                        </button>
                                                    ) : null */}
                                                    {statusInfo.label === 'Delivered' && (
                                                        <Link
                                                            to="/account?tab=reviews"
                                                            className="btn btn-sm btn-outline-main py-4 px-10 rounded-8 text-[11px] fw-bold d-inline-flex align-items-center gap-4"
                                                        >
                                                            <i className="ph-bold ph-star text-xs" /> Review Item
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Logistics info block */}
                            <div className="mt-20 p-14 rounded-12 d-flex align-items-center gap-12 flex-wrap flex-sm-nowrap" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                <div className="flex-center rounded-circle bg-white text-main-600 shadow-xs flex-shrink-0" style={{ width: 42, height: 42 }}>
                                    <i className="ph ph-truck text-2xl" />
                                </div>
                                <div className="flex-grow-1 min-w-0">
                                    <div className="text-sm fw-bold text-neutral-900">
                                        Courier & Delivery: {order.courierName || order.courier || 'Standard Express'}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-2">
                                        {order.awbCode ? (
                                            <span>Tracking / AWB: <strong>{order.awbCode}</strong></span>
                                        ) : (
                                            <span>Tracking will update once the parcel is handed over to courier.</span>
                                        )}
                                        {order.estimatedDeliveryDays && (
                                            <span className="ms-sm-10 d-block d-sm-inline mt-1 mt-sm-0">• Est: <strong>Within {order.estimatedDeliveryDays} business days</strong></span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Order Summary & Addresses */}
                    <div className="col-lg-5">
                        {/* Price Breakdown */}
                        <div className="od-card mb-24">
                            <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom border-gray-100">
                                <h6 className="od-card-title">
                                    <i className="ph ph-receipt text-main-600 text-xl" />
                                    <span>Price Breakdown</span>
                                </h6>
                            </div>

                            <div className="d-flex justify-content-between mb-12 text-sm">
                                <span className="text-gray-600">Items Subtotal</span>
                                <span className="fw-semibold text-neutral-900">₹{subtotal.toFixed(2)}</span>
                            </div>

                            {couponDiscount > 0 && (
                                <div className="d-flex justify-content-between mb-12 text-sm text-success">
                                    <span>
                                        Coupon Discount {order.couponCode && <span className="badge bg-success-50 text-success-700 ms-4">{order.couponCode}</span>}
                                    </span>
                                    <span className="fw-bold">-₹{couponDiscount.toFixed(2)}</span>
                                </div>
                            )}

                            <div className="d-flex justify-content-between mb-12 text-sm">
                                <span className="text-gray-600">Shipping Charges</span>
                                <span className="fw-semibold text-neutral-900">
                                    {shippingCharge === 0 ? <span className="text-success fw-bold">FREE</span> : `₹${shippingCharge.toFixed(2)}`}
                                </span>
                            </div>

                            {parseFloat(order.taxAmount || order.gstAmount || 0) > 0 && (
                                <div className="d-flex justify-content-between mb-12 text-sm">
                                    <span className="text-gray-600">GST / Taxes</span>
                                    <span className="fw-semibold text-neutral-900">₹{parseFloat(order.taxAmount || order.gstAmount).toFixed(2)}</span>
                                </div>
                            )}

                            <div className="d-flex justify-content-between pt-16 mt-16 border-top text-lg fw-bold text-neutral-900">
                                <span>Total Paid / Payable</span>
                                <span className="text-main-600">₹{finalTotal.toFixed(2)}</span>
                            </div>

                            {order.paymentSummary && order.paymentSummary.value > 0 && (
                                <div className="mt-12 p-10 rounded-8 bg-warning-50 text-warning-800 text-xs flex-between">
                                    <span>{order.paymentSummary.label}:</span>
                                    <strong>₹{parseFloat(order.paymentSummary.value).toFixed(2)}</strong>
                                </div>
                            )}
                        </div>

                        {/* Shipping & Payment Details */}
                        <div className="od-card">
                            <div className="d-flex flex-column gap-20" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div>
                                    <div className="od-info-block">
                                        <div className="od-info-label">
                                            <i className="ph ph-map-pin text-main-600" /> Delivery Address
                                        </div>
                                        <div className="od-info-val">
                                            <div className="fw-bold text-neutral-900">{shippingAddr.fullName || 'Customer'}</div>
                                            <div className="text-xs text-gray-600 mt-4">
                                                {shippingAddr.street}
                                                {shippingAddr.apartment ? `, ${shippingAddr.apartment}` : ''}
                                                <br />
                                                {shippingAddr.city}, {shippingAddr.state} - {shippingAddr.pincode}
                                            </div>
                                            {(shippingAddr.phone || shippingAddr.mobile) && (
                                                <div className="text-xs text-gray-500 mt-6 flex-align gap-4">
                                                    <i className="ph ph-phone" /> {shippingAddr.phone || shippingAddr.mobile}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="od-info-block">
                                        <div className="od-info-label">
                                            <i className="ph ph-credit-card text-main-600" /> Payment Details
                                        </div>
                                        <div className="od-info-val">
                                            <div className="fw-bold text-neutral-900">
                                                {isCod ? 'Cash on Delivery (COD)' : (order.paymentMethod === 'upi' ? 'Online Payment (UPI/Cards)' : (order.paymentMethod || 'Prepaid Online'))}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-4">
                                                Status:{' '}
                                                <span className={`fw-bold ${isCod ? 'text-warning-600' : 'text-success-600'}`}>
                                                    {isCod ? 'Pending at Delivery' : 'Paid & Confirmed'}
                                                </span>
                                            </div>
                                            {order.razorpayPaymentId && (
                                                <div className="text-xs text-gray-400 mt-2">
                                                    Transaction ID: {order.razorpayPaymentId}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Actions Bar */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-12 mt-20 no-print">
                    <div className="d-flex align-items-center gap-10 flex-wrap w-100 w-sm-auto">
                        <Link to="/shop" className="btn btn-main py-10 px-20 rounded-8 font-heading fw-bold text-sm flex-grow-1 flex-sm-grow-0 text-center">
                            <i className="ph ph-shopping-bag me-6" /> Continue Shopping
                        </Link>
                        <Link to="/account?tab=orders" className="btn btn-outline-main py-10 px-20 rounded-8 font-heading fw-bold text-sm flex-grow-1 flex-sm-grow-0 text-center">
                            <i className="ph ph-list-dashes me-6" /> All Orders
                        </Link>
                    </div>
                    {canCancel && (
                        <button
                            type="button"
                            onClick={handleCancelOrder}
                            className="btn-cancel-order py-10 px-20 font-heading fw-bold w-100 w-sm-auto justify-content-center"
                            style={{ fontSize: '13px' }}
                        >
                            <i className="ph-bold ph-x-circle text-base" /> Cancel This Order
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

export default OrderDetails;
